import i18n from '@ohif/i18n';

import type { DiscussionTargetType } from '../../../helpers/api/discussions/discussions';
import {
  formatCollaborationLocationLabel,
  localizeCollaborationTargetLabel,
} from './collaboration-labels';

const CLINICAL_NS = 'ClinicalCollaboration';

export type CollaborationLocation =
  | 'study'
  | 'finding'
  | 'frame'
  | 'view'
  | 'report'
  | 'serie'
  | 'generalViewer';

export interface CollaborationTarget {
  type: DiscussionTargetType;
  ref: string;
  label: string;
  location: CollaborationLocation;
}

export type CollaborationScope = 'location' | 'all';

export function buildStudyTarget(studyInstanceUid: string): CollaborationTarget {
  return {
    type: 'STUDY',
    ref: studyInstanceUid,
    label: i18n.t('targets.study', { ns: CLINICAL_NS, defaultValue: 'Exam' }),
    location: 'study',
  };
}

export function buildFindingTarget(
  findingId: number | string,
  title: string,
  result?: string,
  fieldKey?: string
): CollaborationTarget {
  const ref =
    fieldKey != null && fieldKey !== ''
      ? `${findingId}:${fieldKey}`
      : String(findingId);

  return {
    type: 'FINDING',
    ref,
    label: result ? `${title}: ${result}` : title,
    location: 'finding',
  };
}

export function buildFrameTarget(
  frameId: string,
  label: string
): CollaborationTarget {
  return {
    type: 'FRAME',
    ref: frameId,
    label: localizeCollaborationTargetLabel(label),
    location: 'frame',
  };
}

export function buildGeneralViewerFrameTarget(
  frameId: string,
  frameNumber: number
): CollaborationTarget {
  return {
    type: 'FRAME',
    ref: frameId,
    label: i18n.t('location.generalViewerFrame', {
      ns: CLINICAL_NS,
      frame: frameNumber,
      defaultValue: `General viewer — image ${frameNumber}`,
    }),
    location: 'generalViewer',
  };
}

function toDisplayFrameNumber(frameNumber?: number | string): number | string | undefined {
  if (frameNumber == null) return undefined;
  const parsed =
    typeof frameNumber === 'number' ? frameNumber : Number(frameNumber);
  if (Number.isFinite(parsed)) return parsed + 1;
  return frameNumber;
}

export function buildViewFrameTarget(
  viewName: string,
  frameId: string,
  frameNumber?: number | string,
  viewCode?: string
): CollaborationTarget {
  const displayFrameNumber = toDisplayFrameNumber(frameNumber);
  const label = formatCollaborationLocationLabel({
    target_type: 'FRAME',
    view_name: viewName,
    view_code: viewCode,
    frame_number:
      displayFrameNumber != null && Number.isFinite(Number(displayFrameNumber))
        ? Number(displayFrameNumber)
        : null,
  });
  return {
    type: 'FRAME',
    ref: frameId,
    label,
    location: 'view',
  };
}

export function buildReportTarget(reportId: number | string): CollaborationTarget {
  return {
    type: 'REPORT',
    ref: String(reportId),
    label: i18n.t('targets.report', { ns: CLINICAL_NS, defaultValue: 'Report' }),
    location: 'report',
  };
}

const TARGET_LOCATION: Record<DiscussionTargetType, CollaborationLocation> = {
  STUDY: 'study',
  SERIE: 'serie',
  FRAME: 'frame',
  FINDING: 'finding',
  REPORT: 'report',
};

/** Build a panel target from notification payload (target labels resolved server-side in thread list). */
export function buildTargetFromNotificationData(
  targetType: string,
  targetRef: string,
  studyInstanceUid: string,
  targetLabel?: string | null
): CollaborationTarget {
  const type = targetType.toUpperCase() as DiscussionTargetType;
  const label = targetLabel?.trim()
    ? localizeCollaborationTargetLabel(targetLabel)
    : formatCollaborationLocationLabel({ target_type: type });
  return {
    type,
    ref: type === 'STUDY' ? studyInstanceUid : targetRef,
    label,
    location: TARGET_LOCATION[type] ?? 'study',
  };
}

/** Resolve a collaboration target label for display (handles legacy English strings). */
export function resolveCollaborationTargetLabel(label: string): string {
  return localizeCollaborationTargetLabel(label);
}
